import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as Logout } from '../../assets/icons/profile/logout.svg';
import DarkMode from '../../assets/images/settings/DarkMode.png';
import LightMode from '../../assets/images/settings/LightMode.png';
import ThemeContext from '../../contexts/theme-context/ThemeContext';
import { useAppDispatch } from '../../redux/hooks';
import MyButton from '../../shared-components/button/Button';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import RadioButton from '../../shared-components/radio-button/RadioButton';
import SelectField from '../../shared-components/select-field/SelectField';
import { getCookie, setCookie, removeCookie } from '../../shared-functions/Cookies';
import { getLocalStorageItem, removeLocalStorageItem } from '../../shared-functions/LocalStorage';
import auth from '../../utils/api/partner/auth';


import LogoutCancelWarning from './Logout';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
  header: boolean;
}

const themeMap: Record<string, string> = {
  lightMode: 'light',
  darkMode: 'dark',
  systemMode: 'system-default',
};

/**
 * General settings data component
 * @returns {any} - The General settings component.
 */
function General() {
  const { t, i18n } = useTranslation('setting');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
    header: false,
  });

  const { theme, setTheme } = useContext(ThemeContext);

  const [general, setGeneral] = useState({
    themeMode: theme === 'light' ? 'lightMode' : 'darkMode',
    language: 'en',
  });

  useEffect(() => {
    const savedLang = getCookie('i18next') || i18n.language || 'en';

    i18n.changeLanguage(savedLang);

    setGeneral((previous) => ({
      ...previous,
      language: savedLang,
    }));

    // eslint-disable-next-line unicorn/prevent-abbreviations
    const dir = savedLang === 'ar' ? 'rtl' : 'ltr';
    document.body.setAttribute('dir', dir);
  }, []);

  /**
   * function naming
   * @param {any} name - The name
   * @param {any} value - The value
   */
  const handleInputChange = (name: string, value: string | number) => {
    if (name === 'themeMode') {
      const themeValue = themeMap[value as string];
      setTheme(themeValue);
    }

    // 🚀 EXACT LANGUAGE LOGIC from LanguageSelector.jsx
    else if (name === 'language') {
      i18n.changeLanguage(value as string);

      // Set cookie
      setCookie('i18next', value as string, null, null);

      // RTL / LTR direction
      // eslint-disable-next-line unicorn/prevent-abbreviations
      const dir = value === 'ar' ? 'rtl' : 'ltr';
      document.body.setAttribute('dir', dir);
    }

    setGeneral((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * Clear session tokens and navigate to the relative root url
   */
  const clearSessionAndRedirect = () => {
    removeLocalStorageItem('ev-refresh-token');
    removeLocalStorageItem('ev-access-token');
    removeLocalStorageItem('accountVerificationStatus');
    removeLocalStorageItem('activePortal');
    removeLocalStorageItem('userVerification');
    removeCookie('ev-refresh-token');
    removeCookie('ev-access-token');
    dispatch({ type: 'RESET_STORE' });
    navigate('../../');
  };

  /**
   * Function to handle logout API and token deletion from cookies
   */
  const handleTokenDelete = () => {
    const accessToken = getLocalStorageItem('ev-access-token') || '';
    auth
      .logout({ accessToken })
      .then(() => {
        clearSessionAndRedirect();
      })
      .catch((error: unknown) => {
        console.error('Partner logout API failed, logging out locally anyway:', error);
        clearSessionAndRedirect();
      });
  };

  /**
   * Logout popup open functionality
   */
  const handleLogout = () => {
    setModalBoxDetail({
      title: '',
      content: (
        <LogoutCancelWarning
          title={t('logoutTitle')}
          description={t('logoutDescription')}
          cancelButtonLabel={t('cancel')}
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitButtonLabel={t('ok')}
          submitButtonOnClick={handleTokenDelete}
        />
      ),
      notificationModal: true,
      header: false,
    });
    setModalBoxStatus('open');
  };

  return (
    <div className="general-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        header={modalBoxDetail.header}
        notification={modalBoxDetail.notificationModal}
      />
      <div className="general-container">
        <div className="general-top">
          <div className="text-group">
            <p>{t('language')}</p>
            <p className="description">{t('languageDescription')}</p>
          </div>
          <SelectField
            name="language"
            placeholder={t('chooseLanguage')}
            options={[
              { label: t('english'), value: 'en' },
              { label: t('arabic'), value: 'ar' },
              { label: t('french'), value: 'fr' },
              { label: t('spanish'), value: 'sp' },
            ]}
            selectedValue={general.language}
            onChangeValue={handleInputChange}
          />
        </div>
        <div className="general-line" />
        <div className="general-middle">
          <div className="text-group">
            <p>{t('interfaceTheme')}</p>
            <p className="description">{t('interfaceThemeDescription')}</p>
          </div>
          <div className="theme-group">
            <div className="theme-container">
              <img src={LightMode} alt="Light Mode" />
              <RadioButton
                name="themeMode"
                value={general.themeMode}
                list={[{ label: t('lightMode'), value: 'lightMode' }]}
                onChangeValue={handleInputChange}
              />
            </div>

            <div className="theme-container">
              <img src={DarkMode} alt="Dark Mode" />
              <RadioButton
                name="themeMode"
                value={general.themeMode}
                list={[{ label: t('darkMode'), value: 'darkMode' }]}
                onChangeValue={handleInputChange}
              />
            </div>
          </div>
        </div>
        <div className="general-line" />
        <div className="general-bottom">
          <MyButton
            label={t('logout')}
            disableHoverEffect
            buttonType="back"
            onClickFunc={handleLogout}
            svgBackIcon={<Logout />}
          />
        </div>
      </div>
    </div>
  );
}

export default General;
